<template>
  <!--
    Aba "Demandas urgentes" (D38). O QueueKanban do design system não tem arraste: o arraste é
    feito aqui por delegação (dragover/drop no contêiner + data-testid da coluna), só para
    perguntar ao backend e, se ele negar, explicar no pop-up (D43). A coluna é sempre derivada.
  -->
  <div class="flex flex-col gap-4 text-left" data-testid="pre-allocation-kanban-tab">
    <EmptyState
      v-if="isUnavailable"
      :icon="mdiViewColumnOutline"
      title="Demandas urgentes indisponíveis"
      description="A revisão de documentos ainda não foi liberada neste ambiente."
      variant="dashed"
    />
    <EmptyState
      v-else-if="hasNoScope"
      :icon="mdiLockOutline"
      title="Nenhuma escala para acompanhar"
      description="Você não tem acesso a nenhuma escala com revisão de documentos. Peça a um administrador da sua empresa para liberar o acesso."
      variant="dashed"
    />
    <template v-else>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-[13px] text-[#52526f] max-w-3xl">
          Médicos <b class="text-textPrimary">pré-alocados</b> esperando documentação. Ordenado pelo
          plantão mais próximo. Os cartões andam sozinhos conforme o status dos documentos.
        </p>
        <div class="flex items-center gap-3 text-[11px] text-[#52526f]">
          <span class="flex items-center gap-1">
            <span class="w-2.5 h-2.5 rounded-sm bg-red-50 border border-red-300" />
            ≤ 24h do plantão
          </span>
          <span class="flex items-center gap-1">
            <span class="w-2.5 h-2.5 rounded-sm bg-orange-50 border border-orange-300" />
            ≤ 72h
          </span>
        </div>
      </div>

      <div
        v-if="isLoading && !isLoaded"
        class="h-[320px] rounded-lg bg-gray-100 animate-pulse"
        data-testid="pre-allocation-kanban-skeleton"
      />
      <ReportErrorState
        v-else-if="loadError"
        :title="KANBAN_LOAD_ERROR"
        :message="loadError === KANBAN_LOAD_ERROR ? null : loadError"
        @retry="store.fetch()"
      />
      <EmptyState
        v-else-if="!sortedCards.length"
        :icon="mdiViewColumnOutline"
        title="Nenhuma demanda urgente"
        description="Quando um médico for pré-alocado com documento pendente ou em análise, a demanda aparece aqui."
        variant="dashed"
      />
      <div v-else @dragover.prevent @drop.prevent="onDrop" @dragend="draggingId = null">
        <QueueKanban
          :rows="sortedCards"
          :columns="columns"
          :row-key="(row: PreAllocationCard) => row.id"
          empty-column-text="Nenhuma demanda nesta etapa."
          :test-id="KANBAN_TEST_ID"
        >
          <template #card="{ row }">
            <PreAllocationCardView
              :card="row"
              :is-moving="movingId === row.id"
              :can-move="canReview"
              @drag-start="draggingId = $event.id"
              @try-resolve="tryMove($event, PreAllocationColumn.RESOLVED)"
            />
          </template>
        </QueueKanban>
      </div>
    </template>

    <PreAllocationBlockedModal
      v-model="blocked.open"
      :professional-name="blocked.card?.professional.name ?? ''"
      :target-column="blocked.target"
      :items="blocked.items"
      :nearest-station="blocked.card ? (stationsByStart(blocked.card)[0] ?? null) : null"
      @review="onReview"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiLockOutline, mdiViewColumnOutline } from '@mdi/js'

import EmptyState from '@/components/designSystem/EmptyState/EmptyState.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import QueueKanban from '@/components/designSystem/QueueViews/QueueKanban.vue'
import type { QueueKanbanColumn } from '@/components/designSystem/QueueViews/types'
import { pendingReasonText } from '@/components/ProfessionalDocuments/labels'
import { useErrorToast } from '@/composables/useErrorToast'
import { usePreAllocationKanbanStore } from '@/pinia-store/usePreAllocationKanbanStore'
import { useProfessionalDocumentCatalogStore } from '@/pinia-store/useProfessionalDocumentCatalogStore'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import { PreAllocationColumn } from '@/types/qualification-eligibility.types'
import type { EligibilityReason, PreAllocationCard } from '@/types/qualification-eligibility.types'

import { useDocumentReviewAccess } from '../composables/useDocumentReviewAccess'
import { KANBAN_LOAD_ERROR, MOVE_ERROR } from '../constants'
import {
  KANBAN_TEST_ID,
  PRE_ALLOCATION_COLUMNS,
  parseKanbanColumnKey,
  stationsByStart
} from '../model'
import PreAllocationBlockedModal from './PreAllocationBlockedModal.vue'
import type { BlockedItem } from './PreAllocationBlockedModal.vue'
import PreAllocationCardView from './PreAllocationCard.vue'

const emit = defineEmits<{
  /** "Revisar documentos" no pop-up: o pai abre a aba Documentos (pode filtrar pelo médico). */
  reviewDocuments: [professional: PreAllocationCard['professional']]
}>()

const store = usePreAllocationKanbanStore()
const { canReview } = useDocumentReviewAccess()
const catalog = useProfessionalDocumentCatalogStore()
const { sortedCards, isLoading, isLoaded, isUnavailable, hasNoScope, loadError, movingId } =
  storeToRefs(store)
const { notifyError } = useErrorToast()

const columns: QueueKanbanColumn<PreAllocationCard>[] = PRE_ALLOCATION_COLUMNS.map((column) => ({
  ...column,
  matches: (card: PreAllocationCard) => card.column === column.key
}))

const draggingId = ref<number | null>(null)
const blocked = reactive<{
  open: boolean
  card: PreAllocationCard | null
  target: PreAllocationColumn
  items: BlockedItem[]
}>({ open: false, card: null, target: PreAllocationColumn.RESOLVED, items: [] })

const sameReason = (a: EligibilityReason, b: EligibilityReason) =>
  a.field === b.field && a.key === b.key

/** Nome que o cartão já trouxe; item novo (cartão desatualizado) busca no catálogo. */
async function toBlockedItems(
  card: PreAllocationCard,
  missing: EligibilityReason[]
): Promise<BlockedItem[]> {
  const nameInCard = (reason: EligibilityReason) =>
    card.missing.find((item) => sameReason(item, reason))?.name
  if (missing.some((reason) => !nameInCard(reason))) {
    // fallback intencional: sem catálogo o pop-up mostra a chave do item, que segue visível
    await catalog.ensureLoaded().catch(() => undefined)
  }
  return missing.map((reason) => {
    const status = reason.status ?? ProfessionalDocumentStatus.PENDING
    return {
      key: `${reason.field}:${reason.key}`,
      name: nameInCard(reason) ?? catalog.reasonName(reason),
      status,
      detail: pendingReasonText({
        status,
        pendingReason: reason.pendingReason,
        expiresAt: reason.expiresAt
      })
    }
  })
}

async function tryMove(card: PreAllocationCard, target: PreAllocationColumn) {
  if (!canReview.value) return
  try {
    const outcome = await store.move(card, target)
    if (outcome.kind !== 'blocked') return
    const items = await toBlockedItems(card, outcome.missing)
    Object.assign(blocked, { open: true, card, target, items })
  } catch (error) {
    notifyError(error, MOVE_ERROR)
  }
}

function onDrop(event: DragEvent) {
  const card = sortedCards.value.find((item) => item.id === draggingId.value)
  draggingId.value = null
  const columnEl = (event.target as HTMLElement | null)?.closest(
    `[data-testid^="${KANBAN_TEST_ID}-column-"]`
  )
  const target = parseKanbanColumnKey(columnEl?.getAttribute('data-testid') ?? null)
  if (card && target) void tryMove(card, target)
}

function onReview() {
  blocked.open = false
  if (blocked.card) emit('reviewDocuments', blocked.card.professional)
}

// Começa no setup: o primeiro render já sai com o skeleton, sem piscar o vazio (R25).
void store.fetch()
</script>
